// Сборка сервисов клиента: навигация, полоса действия, сеть, сессия, отчёты об ошибках, вибрация.
// Один экземпляр на запуск; экраны берут его через useApp().
import { signal } from '@preact/signals';
import { BottomBar, bindTelegramButtons } from '../nav/bottom-bar.js';
import { Navigator } from '../nav/navigator.js';
import { ApiCallError, createApiClient, uuid7, type ApiCall } from '../net/api-client.js';
import { policyFor, type ErrorPolicy } from '../net/error-policy.js';
import { SessionStore } from '../net/session.js';
import { ErrorReporter } from '../errors/reporter.js';
import { createHaptics, type HapticKind } from '../telegram/haptics.js';
import type { TgWebApp } from '../telegram/webapp.js';
import { backOnline, online, serverOffset } from '../state/game.js';
import { storage } from '../state/storage.js';
import { showToast } from '../ui/toast.js';
import { REQUIREMENT_REASONS } from '../net/error-policy.js';
import { t as tr } from '../i18n/t.js';

export const CLIENT_BUILD = '0.1.0';
/** Версия и метка сборки — ctx аналитики, заголовок клиента: устаревший билд из кэша виден на сервере. */
export const BUILD_TAG = `${CLIENT_BUILD}+${typeof __BUILD_ID__ === 'string' ? __BUILD_ID__ : 'dev'}`.slice(0, 32);

export interface AppDeps {
  fetch: typeof fetch;
  wa: TgWebApp | null;
  glitchtipDsn?: string;
}

/** Плашка «Перезапустить игру?» после 3 ошибок за минуту (ux/11 §4 ST-ERROR — единое правило). */
export const restartHint = signal(false);
export const ERROR_WINDOW_MS = 60_000;

export function createApp(deps: AppDeps) {
  const nav = new Navigator();
  const bar = new BottomBar();
  const session = new SessionStore();
  const navVersion = signal(0);
  const vibration = () => storage.get('vibration') !== 'off';
  const haptic = createHaptics({ wa: () => deps.wa, enabled: vibration, vibrate: (p) => navigator.vibrate?.(p) });

  let fails = 0;
  const onNetwork = (ok: boolean) => {
    if (ok) {
      fails = 0;
      if (!online.value) { online.value = true; backOnline.value = true; setTimeout(() => { backOnline.value = false; }, 1500); }
    } else if (++fails >= 2) online.value = false; // два запроса подряд не прошли (ux/11 §4 ST-OFFLINE)
  };

  let call: ApiCall = null as unknown as ApiCall;
  call = createApiClient({
    fetch: deps.fetch,
    session: session.current,
    clientBuild: `${CLIENT_BUILD}+web`,
    refresh: () => session.refresh(call)(),
    onNetwork,
    onServerTime: (srv, local) => { serverOffset.value = srv - local; },
  });

  // 426 CLIENT_TOO_OLD и 403 BANNED с ЛЮБОГО запроса — системный экран сразу, даже если экран ошибку не разбирает
  // (E02-T07, ux/11 §3.3, §3.5). Экраны дальше получают ошибку как обычно.
  const raw = call;
  call = (async (name, opts) => {
    try { return await raw(name, opts); } catch (e) {
      if (e instanceof ApiCallError && (e.code === 'CLIENT_TOO_OLD' || e.code === 'BANNED')) {
        const id = e.code === 'BANNED' ? 'SCR-BANNED' : 'SCR-UPDATE-REQUIRED';
        if (nav.state.system?.id !== id) nav.showSystem(id, { code: e.code, details: e.details });
      }
      throw e;
    }
  }) as ApiCall;

  const reporter = new ErrorReporter({
    build: CLIENT_BUILD,
    screen: () => nav.state.system?.id ?? nav.state.sheets.at(-1)?.id ?? nav.top(),
    ready: () => !!session.current(),
    send: async (sessionId, errors) => { await call('clientErrors', { body: { sessionId, errors }, noRetry: true }); },
    onFatal: (code) => nav.showSystem('SCR-FATAL', { code }),
    ...(deps.glitchtipDsn ? { glitchtipDsn: deps.glitchtipDsn } : {}),
  });

  const recentErrors: number[] = [];
  const noteError = () => {
    const t = Date.now();
    recentErrors.push(t);
    while (recentErrors.length && t - recentErrors[0]! > ERROR_WINDOW_MS) recentErrors.shift();
    if (recentErrors.length >= 3) restartHint.value = true;
  };

  /** Хук входа заново (SIGNATURE_INVALID один раз — тихий повторный вход, ux/11 §4.1). Ставит boot. */
  let relogin: () => Promise<boolean> = async () => false;

  /**
   * Показать ошибку по таблице ux/11 §4.1. Возвращает политику — экран может дописать текст у кнопки (inline).
   * `resource` — что «на месте» в тексте INTERNAL («Гайки на месте»).
   */
  function handleError(e: unknown, o: { resource?: string; retry?: () => void } = {}): ErrorPolicy {
    if (!(e instanceof ApiCallError)) {
      reporter.report('js', e);
      return policyFor('INTERNAL');
    }
    const p = policyFor(e.code);
    if (p.report) reporter.report('network', new Error(`${e.endpoint ?? '?'} ${e.code}`));
    switch (p.surface) {
      case 'system':
        nav.showSystem(p.system!, { code: e.code, details: e.details, retryAfterS: e.retryAfterS, text: p.text });
        break;
      case 'modal':
        nav.openSheet(p.modal!, { code: e.code, details: e.details, text: p.text });
        break;
      case 'relogin':
        void relogin().then((ok) => { if (!ok) nav.showSystem('SCR-BOOT', { code: 'INIT_DATA_EXPIRED', text: policyFor('INIT_DATA_EXPIRED').text }); });
        break;
      case 'toast':
      case 'inline': {
        let text = p.text ?? tr('CL-APP-APP-01');
        if (e.code === 'INTERNAL' || e.code === 'BAD_RESPONSE') text = tr('CL-APP-APP-02', { p0: o.resource ?? tr('CL-APP-APP-ALL') });
        if (e.code === 'RATE_LIMITED' && e.retryAfterS) text = tr('CL-APP-APP-03', { p0: e.retryAfterS });
        if (e.code === 'REQUIREMENT_NOT_MET') text = REQUIREMENT_REASONS[String(e.details?.['reason'] ?? '')] ?? text;
        if (p.surface === 'toast' || !o.retry) {
          showToast(text, 'error', o.retry && (e.code === 'INTERNAL' || e.code === 'NETWORK' || e.code === 'TIMEOUT') ? { text: tr('CL-APP-APP-04'), onClick: o.retry } : undefined);
          haptic('error');
        }
        noteError();
        break;
      }
      default:
        break;
    }
    return p;
  }

  const syncButtons = bindTelegramButtons(deps.wa, nav, () => nav.openService('SCR-SETTINGS'));
  nav.subscribe(() => { navVersion.value++; syncButtons(); });

  return {
    nav, bar, session, haptic, reporter, wa: deps.wa, navVersion,
    get call() { return call; },
    handleError,
    setRelogin(fn: () => Promise<boolean>) { relogin = fn; },
    idem: uuid7,
    syncButtons,
  };
}

export type App = ReturnType<typeof createApp>;
export type { HapticKind };

let current: App | null = null;
export function setApp(a: App): void { current = a; }
export function useApp(): App {
  if (!current) throw new Error('app не создан');
  return current;
}
